import type { AuthSession, SessionUser } from './types';

export class AuthApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: string,
  ) {
    super(`${status} ${code}${detail === undefined ? '' : `: ${detail}`}`);
    this.name = 'AuthApiError';
  }
}

export interface AuthClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export interface AuthorizedResult {
  response: Response;
  session: AuthSession;
}

interface ProblemPayload {
  code?: unknown;
  detail?: unknown;
}

const DEFAULT_BASE_URL = '/api/v1';

/**
 * Thin HTTP client for the auth API. The refresh loop is deliberately in one
 * place: a 401 triggers exactly one refresh, the retry either succeeds or the
 * caller receives the 401 and the provider drops the session (-> login).
 */
export class AuthClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AuthClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, '');
    const injected = options.fetchImpl;
    this.fetchImpl = injected ?? ((...args) => globalThis.fetch(...args));
  }

  async login(email: string, password: string): Promise<AuthSession> {
    const response = await this.send(undefined, '/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return this.parseSession(response);
  }

  async refresh(refreshToken: string): Promise<AuthSession> {
    const response = await this.send(undefined, '/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return this.parseSession(response);
  }

  async logout(refreshToken: string): Promise<void> {
    await this.send(undefined, '/auth/logout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  }

  async me(accessToken: string): Promise<SessionUser> {
    const response = await this.send(accessToken, '/auth/me');
    if (!response.ok) {
      throw await this.toError(response);
    }
    return (await response.json()) as SessionUser;
  }

  async authorizedFetch(
    session: AuthSession,
    path: string,
    init: RequestInit = {},
  ): Promise<AuthorizedResult> {
    const first = await this.send(session.accessToken, path, init);
    if (first.status !== 401) {
      return { response: first, session };
    }

    const rotated = await this.refresh(session.refreshToken);
    const retried = await this.send(rotated.accessToken, path, init);
    if (retried.status === 401) {
      throw new AuthApiError(401, 'auth.unauthenticated', 'The session ended while refreshing.');
    }
    return { response: retried, session: rotated };
  }

  private send(accessToken: string | undefined, path: string, init: RequestInit = {}): Promise<Response> {
    const headers: Record<string, string> =
      init.headers === undefined ? {} : ({ ...(init.headers as Record<string, string>) } as Record<string, string>);
    if (accessToken !== undefined) {
      headers.authorization = `Bearer ${accessToken}`;
    }
    return this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });
  }

  private async parseSession(response: Response): Promise<AuthSession> {
    if (!response.ok) {
      throw await this.toError(response);
    }
    return (await response.json()) as AuthSession;
  }

  private async toError(response: Response): Promise<AuthApiError> {
    let payload: ProblemPayload = {};
    try {
      payload = (await response.json()) as ProblemPayload;
    } catch {
      payload = {};
    }
    const code = typeof payload.code === 'string' ? payload.code : 'auth.request-failed';
    const detail = typeof payload.detail === 'string' ? payload.detail : undefined;
    return new AuthApiError(response.status, code, detail);
  }
}
